import pythonApiClient from "./pythonBaseApi";

const depositosApi = {
  /**
   * Obtener todos los tipos de depósito
   * @param {boolean} soloActivos - Si es false, incluye inactivos (mantenimiento)
   */
  getTiposDeposito: (soloActivos = true) => {
    return pythonApiClient.get("/corporativo/depositos/tipos", {
      params: { solo_activos: soloActivos },
    });
  },

  getTipoDepositoById: (id) =>
    pythonApiClient.get(`/corporativo/depositos/tipos/${id}`),

  createTipoDeposito: (data) =>
    pythonApiClient.post("/corporativo/depositos/tipos", data),

  updateTipoDeposito: (id, data) =>
    pythonApiClient.put(`/corporativo/depositos/tipos/${id}`, data),

  deleteTipoDeposito: (id) =>
    pythonApiClient.delete(`/corporativo/depositos/tipos/${id}`),

  getRetencionProveedorParametros: () =>
    pythonApiClient.get("/corporativo/depositos/retencion-parametros"),

  updateRetencionProveedorParametros: (data) =>
    pythonApiClient.put("/corporativo/depositos/retencion-parametros", data),

  getCentrosCosto: (soloActivos = true) => {
    return pythonApiClient.get("/corporativo/centros-costo", {
      params: { solo_activos: soloActivos },
    });
  },

  createCentroCosto: (data) => pythonApiClient.post("/corporativo/centros-costo", data),

  updateCentroCosto: (id, data) =>
    pythonApiClient.put(`/corporativo/centros-costo/${id}`, data),

  deleteCentroCosto: (id) => pythonApiClient.delete(`/corporativo/centros-costo/${id}`),

  /**
   * Obtener lista de depósitos con filtros y paginación
   * @param {Object} filters - Filtros de búsqueda
   * @returns {Promise} Lista de depósitos
   */
  getDepositos: (filters = {}) => {
    return pythonApiClient.get("/corporativo/depositos", {
      params: filters,
    });
  },

  /**
   * Obtener depósito por ID
   * @param {number} depositoId - ID del depósito
   * @returns {Promise} Datos del depósito
   */
  getDepositoById: (depositoId) => {
    return pythonApiClient.get(`/corporativo/depositos/${depositoId}`);
  },

  /**
   * Crear nuevo depósito
   * @param {Object} depositoData - Datos del depósito
   * @returns {Promise} Depósito creado
   */
  createDeposito: (depositoData) => {
    return pythonApiClient.post("/corporativo/depositos", depositoData);
  },

  /**
   * Actualizar depósito
   * @param {number} depositoId - ID del depósito
   * @param {Object} depositoData - Datos a actualizar
   * @returns {Promise} Depósito actualizado
   */
  updateDeposito: (depositoId, depositoData) => {
    return pythonApiClient.put(`/corporativo/depositos/${depositoId}`, depositoData);
  },

  /**
   * Eliminar depósito
   * @param {number} depositoId - ID del depósito
   * @returns {Promise} Resultado de la eliminación
   */
  deleteDeposito: (depositoId) => {
    return pythonApiClient.delete(`/corporativo/depositos/${depositoId}`);
  },

  /**
   * Aprobar depósito
   * @param {number} depositoId - ID del depósito
   * @returns {Promise} Depósito aprobado
   */
  aprobarDeposito: (depositoId) => {
    return pythonApiClient.post(`/corporativo/depositos/${depositoId}/aprobar`);
  },

  /** Revoca aprobación (vuelve a registrado / editable). Requiere depositos.aprobar */
  desaprobarDeposito: (depositoId) => {
    return pythonApiClient.post(`/corporativo/depositos/${depositoId}/desaprobar`);
  },

  /**
   * Cancelar depósito
   * @param {number} depositoId - ID del depósito
   * @returns {Promise} Depósito cancelado
   */
  cancelarDeposito: (depositoId) => {
    return pythonApiClient.post(`/corporativo/depositos/${depositoId}/cancelar`);
  },

  /**
   * Marcar depósito como depositado (multipart: fecha_pago, metodo_pago, numero_operacion, comprobante opcional)
   * @param {number} depositoId - ID del depósito
   * @param {FormData} formData - FormData con campos del formulario de confirmación
   * @returns {Promise} Depósito actualizado
   */
  marcarDepositado: (depositoId, formData) => {
    return pythonApiClient.post(
      `/corporativo/depositos/${depositoId}/marcar-depositado`,
      formData
    );
  },

  /** Solo si está Realizado (aprobado + fecha pago): limpia pago y comprobante; queda Aprobado de nuevo */
  desmarcarDepositado: (depositoId) => {
    return pythonApiClient.post(`/corporativo/depositos/${depositoId}/desmarcar-depositado`);
  },

  /** Obtener imagen del comprobante (blob; requiere token en axios) */
  getComprobanteImagenDeposito: (depositoId) => {
    return pythonApiClient.get(`/corporativo/depositos/${depositoId}/comprobante-imagen`, {
      responseType: 'blob',
    });
  },

  // ========== BENEFICIARIOS ==========
  getBeneficiarios: (tipo = null, soloActivos = true) => {
    const params = {};
    if (tipo) params.tipo = tipo;
    if (!soloActivos) params.incluir_inactivos = true;
    return pythonApiClient.get("/corporativo/depositos/beneficiarios", { params });
  },

  getBeneficiarioById: (beneficiarioId) => {
    return pythonApiClient.get(`/corporativo/depositos/beneficiarios/${beneficiarioId}`);
  },

  createBeneficiario: (beneficiarioData) => {
    return pythonApiClient.post("/corporativo/depositos/beneficiarios", beneficiarioData);
  },

  createBeneficiarioConCuentas: (beneficiarioData) => {
    return pythonApiClient.post("/corporativo/depositos/beneficiarios/con-cuentas", beneficiarioData);
  },

  updateBeneficiario: (beneficiarioId, beneficiarioData) => {
    return pythonApiClient.put(`/corporativo/depositos/beneficiarios/${beneficiarioId}`, beneficiarioData);
  },

  deleteBeneficiario: (beneficiarioId) => {
    return pythonApiClient.delete(`/corporativo/depositos/beneficiarios/${beneficiarioId}`);
  },

  // ========== CUENTAS BANCARIAS ==========
  getCuentasByBeneficiario: (beneficiarioId, activo = true, todas = false) => {
    const params = { activo, todas };
    return pythonApiClient.get(`/corporativo/depositos/beneficiarios/${beneficiarioId}/cuentas`, {
      params,
    });
  },

  getCuentaById: (cuentaId) => {
    return pythonApiClient.get(`/corporativo/depositos/cuentas/${cuentaId}`);
  },

  createCuentaBancaria: (cuentaData) => {
    return pythonApiClient.post("/corporativo/depositos/cuentas", cuentaData);
  },

  updateCuentaBancaria: (cuentaId, cuentaData) => {
    return pythonApiClient.put(`/corporativo/depositos/cuentas/${cuentaId}`, cuentaData);
  },

  deleteCuentaBancaria: (cuentaId) => {
    return pythonApiClient.delete(`/corporativo/depositos/cuentas/${cuentaId}`);
  },

  // ========== TIPOS DE CUENTA BANCARIA ==========
  getTiposCuentaBancaria: (activo = true) => {
    return pythonApiClient.get("/corporativo/depositos/tipos-cuenta-bancaria", {
      params: { activo }
    });
  },

  // ========== EXPORTAR PDF ==========
  /**
   * Exportar depósitos a PDF
   * @param {Object} filters - Filtros de búsqueda
   * @returns {Promise} Archivo PDF
   */
  exportPDF: (filters = {}) => {
    return pythonApiClient.get("/corporativo/depositos/export/pdf", {
      params: filters,
      responseType: 'blob'
    });
  },

  // ========== EXPORTAR EXCEL ==========
  /**
   * Exportar depósitos a Excel - Reporte Resumen
   * Agrupa por beneficiario sin descripción detallada
   * @param {Object} filters - Filtros de búsqueda
   * @returns {Promise} Archivo Excel
   */
  exportExcelResumen: (filters = {}) => {
    return pythonApiClient.get("/corporativo/depositos/export/excel-resumen", {
      params: filters,
      responseType: 'blob'
    });
  },

  /**
   * Exportar depósitos a Excel - Reporte Detallado
   * Muestra todos los depósitos con descripción completa
   * @param {Object} filters - Filtros de búsqueda
   * @returns {Promise} Archivo Excel
   */
  exportExcelDetallado: (filters = {}) => {
    return pythonApiClient.get("/corporativo/depositos/export/excel-detallado", {
      params: filters,
      responseType: 'blob'
    });
  },

  exportReporteAgrupadoCentroCostoPdf: (filters = {}) => {
    return pythonApiClient.get("/corporativo/depositos/export/pdf-agrupado-centro-costo", {
      params: filters,
      responseType: 'blob'
    });
  },

  /** Excel: por día (fecha solicitud), centro de costo, tipo de depósito y moneda + hoja totales por día */
  exportExcelAgrupadoDiaCentroTipo: (filters = {}) => {
    return pythonApiClient.get("/corporativo/depositos/export/excel-agrupado-dia-centro-tipo", {
      params: filters,
      responseType: "blob",
    });
  },

  /** PDF: por día con detalle CC/tipo, subtotal por día y total general */
  exportPdfAgrupadoDiaCentroTipo: (filters = {}) => {
    return pythonApiClient.get("/corporativo/depositos/export/pdf-agrupado-dia-centro-tipo", {
      params: filters,
      responseType: "blob",
    });
  },

  /** PDF: por día solo por centro de costo, total por día y total general */
  exportPdfResumenDiaCentroCosto: (filters = {}) => {
    return pythonApiClient.get("/corporativo/depositos/export/pdf-resumen-dia-centro-costo", {
      params: filters,
      responseType: "blob",
    });
  },

  /** PDF Gerencia: total por CC en 1.ª hoja; luego agrupado por día y centro de costo */
  exportPdfGerencia: (filters = {}) => {
    return pythonApiClient.get("/corporativo/depositos/export/pdf-gerencia", {
      params: filters,
      responseType: "blob",
    });
  },
};

export default depositosApi;
